import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { ComboboxItem } from '../Combobox/Combobox'
import { SearchSelect } from '../SearchSelect/SearchSelect'
import type { PickerValue } from '../ProductPicker/ProductPicker'

/**
 * res.partner filter. Sprint 01 uses `manufacturer` (is_company=True).
 * Sprint 03 Vendor Pricelists will pass `customer` / `vendor` — the
 * middleware `/api/pricelists/search/partners` endpoint already
 * validates all three values.
 */
export type PartnerKind = 'manufacturer' | 'customer' | 'vendor' | 'customer_contact'

interface PartnerRow {
  id: number
  name: string
  /** customer_contact only — the person's email + their company. */
  email?: string | null
  company_name?: string | null
}

interface PartnersResponse {
  partners: PartnerRow[]
}

export interface PartnerPickerProps {
  value: PickerValue
  onChange: (next: PickerValue) => void
  /** Which subset of `res.partner` to search. Defaults to `manufacturer`. */
  kind?: PartnerKind
  /** customer_contact only — scope the people search to one company. */
  parentId?: number | null
  placeholder?: string
  editorMode?: boolean
  onCancel?: () => void
  width?: number
  className?: string
  disabled?: boolean
  /**
   * `'cell'` (default) — bare/borderless, for use inside an EditableGrid
   * cell or an already-bordered embedded form row, where the surrounding
   * chrome supplies the box. `'field'` — bordered/padded, matching the
   * `Input` primitive, for a standalone picker dropped directly on a page
   * (e.g. a page-level customer search) where there's no other chrome to
   * borrow. Forwarded as-is to the underlying `SearchSelect`.
   */
  variant?: 'cell' | 'field'
}

/**
 * Debounced server-search picker over `/api/pricelists/search/partners`.
 * `kind` filters the partner scope; the same component serves
 * manufacturer (Sprint 01), customer, and vendor (Sprint 03) pickers.
 */
export function PartnerPicker({
  value,
  onChange,
  kind = 'manufacturer',
  parentId,
  placeholder = 'Search companies…',
  editorMode = false,
  onCancel,
  width = 320,
  className,
  disabled,
  variant = 'cell',
}: PartnerPickerProps) {
  const [query, setQuery] = useState<string>('')
  const [debounced, setDebounced] = useState<string>('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])

  const { data, isFetching } = useQuery<PartnersResponse>({
    queryKey: ['ui', 'partner-picker', kind, debounced, parentId ?? null],
    queryFn: () =>
      jsonFetch<PartnersResponse>(
        `/api/pricelists/search/partners?q=${encodeURIComponent(debounced)}&kind=${kind}&limit=20` +
          (parentId ? `&parent_id=${parentId}` : ''),
      ),
    staleTime: 30_000,
  })

  const items: ComboboxItem[] = useMemo(
    () =>
      (data?.partners ?? []).map((p) => {
        // customer_contact rows carry the person's company + email so the
        // operator can disambiguate "Jane Doe · ACME Corp · jane@acme.com".
        const extras = [p.company_name, p.email].filter(Boolean).join(' · ')
        return {
          value: String(p.id),
          label: extras ? `${p.name || `Partner ${p.id}`} · ${extras}` : p.name || `Partner ${p.id}`,
        }
      }),
    [data],
  )

  const handlePicked = (idStr: string) => {
    const id = Number(idStr)
    const row = (data?.partners ?? []).find((p) => p.id === id)
    if (!row) return
    onChange([id, row.name])
  }

  const emptyMessage = isFetching
    ? 'Searching…'
    : debounced
      ? 'No matches'
      : 'Type to search'

  return (
    <SearchSelect
      variant={variant}
      items={items}
      value={value ? String(value[0]) : undefined}
      selectedLabel={value ? value[1] : null}
      onQueryChange={setQuery}
      onSelect={handlePicked}
      onClear={() => onChange(null)}
      editorMode={editorMode}
      onCancel={onCancel}
      width={width}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      shouldFilter={false}
      disabled={disabled}
      className={className}
    />
  )
}
